import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Grid,
  Card,
  CardContent,
  Avatar,
  Chip,
  CircularProgress,
  Alert,
  AlertTitle,
  Paper,
  Container,
  useTheme,
  alpha
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Today as TodayIcon,
  Restaurant as RestaurantIcon,
  BreakfastDining as BreakfastIcon,
  LunchDining as LunchIcon,
  DinnerDining as DinnerIcon,
  Cookie as SnackIcon,
  Refresh as RefreshIcon,
  DinnerDining as DinnerDiningIcon,
} from '@mui/icons-material';
import { styled, keyframes } from '@mui/material/styles';
import axiosInstance from '../../lib/axios';
import { useLocation } from 'react-router-dom';

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const MEAL_MAP = {
  2: { label: 'Breakfast', time: '7:00–9:00 AM',   icon: <BreakfastIcon />,    color: '#F59E0B', bg: '#FFFBEB' },
  3: { label: 'Lunch',     time: '12:30–2:30 PM',  icon: <LunchIcon />,        color: '#10B981', bg: '#ECFDF5' },
  4: { label: 'Snacks',    time: '5:00–6:00 PM',   icon: <SnackIcon />,        color: '#8B5CF6', bg: '#F5F3FF' },
  5: { label: 'Dinner',    time: '7:30–9:30 PM',   icon: <DinnerDiningIcon />, color: '#EF4444', bg: '#FEF2F2' },
};

const MEAL_ORDER  = [2, 3, 4, 5];
const DAYS_FULL   = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const DAYS_SHORT  = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];



const StyledMealCard = styled(Card)(({ mealcolor }) => ({
  borderRadius: 14,
  border: `1px solid ${alpha(mealcolor, 0.2)}`,
  borderTop: `3px solid ${mealcolor}`,
  overflow: 'hidden',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
}));

const StyledDateButton = styled(Button)(({ theme, active, hasdata }) => ({
  minWidth: 88,
  padding: theme.spacing(0.7, 1.75),
  borderRadius: 20,
  textTransform: 'none',
  backgroundColor: active ? '#F59E0B' : '#FFFFFF',
  color: active ? '#FFFFFF' : '#64748B',
  border: active ? 'none' : '1px solid #E2E8F0',
  '&:hover': {
    backgroundColor: active ? '#D97706' : '#F8FAFC',
  },
  position: 'relative',
  '&::after': hasdata
    ? {
        content: '""',
        position: 'absolute',
        bottom: 6,
        left: '50%',
        transform: 'translateX(-50%)',
        width: 6,
        height: 6,
        borderRadius: '50%',
        backgroundColor: active ? 'rgba(255,255,255,0.8)' : '#F59E0B',
      }
    : {},
}));


function getWeekStart(d) {
  const date = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  date.setHours(0, 0, 0, 0);
  return date;
}

function addDays(d, n) {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}


function fmtISO(d) {
  const year  = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day   = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}


function parseLocalDate(dateStr) {
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day);
}

function fmtShort(d) {
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
}

function fmtLong(d) {
  return d.toLocaleDateString('en-IN', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}


function parseMenu(list) {
  const data = {};
  (list || []).forEach(day => {
    const key = day.FoodWeekCalDt?.split('T')[0];
    if (!key) return;

    if (!data[key]) {
      data[key] = { dayName: day.DayNm, remark: day.Remark, meals: {} };
    }

    (day.FoodWeekCalList || []).forEach(item => {
      const mt = item.MealTypeId;
      if (!data[key].meals[mt]) data[key].meals[mt] = [];
      data[key].meals[mt].push(item);
    });
  });
  return data;
}



function FoodCard({ food, mealIcon }) {
  const [imgOk, setImgOk] = useState(true);
  let src = food.FoodImg || food.FoodSubGrpImg || '';

  if (src && src.startsWith('http:/') && !src.startsWith('http://')) {
    src = src.replace('http:/', 'http://');
  }

  return (
    <Paper
      elevation={0}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        p: 0.8,
        borderRadius: 2,
        border: '1px solid #E2E8F0',
        bgcolor: '#F8FAFC',
        mb: 1.5,
        '&:last-child': { mb: 0 },
      }}
    >
      {src && imgOk ? (
        <Box
          component="img"
          src={src}
          alt={food.FoodName}
          onError={() => setImgOk(false)}
          sx={{ width: 30, height: 30, borderRadius: 1.5, objectFit: 'cover', flexShrink: 0 }}
        />
      ) : (
        <Box
          sx={{
            width: 30,
            height: 30,
            borderRadius: 1.5,
            flexShrink: 0,
            bgcolor: '#F1F5F9',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 18,
          }}
        >
          {mealIcon}
        </Box>
      )}
      <Box sx={{ flex: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1E293B' }}>
          {food.FoodName}
        </Typography>
      </Box>
    </Paper>
  );
}



function MealCard({ mealTypeId, foods }) {
  const m = MEAL_MAP[mealTypeId];
  if (!m) return null;

  return (
    <StyledMealCard mealcolor={m.color}>
      <Box
        sx={{
          bgcolor: m.bg,
          p: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: `1px solid ${alpha(m.color, 0.2)}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar sx={{ bgcolor: alpha(m.color, 0.1), color: m.color, width: 32, height: 32 }}>
            {m.icon}
          </Avatar>
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: m.color }}>
              {m.label}
            </Typography>
            <Typography variant="caption" sx={{ color: '#64748B' }}>
              {m.time}
            </Typography>
          </Box>
        </Box>
        <Chip
          label={`${foods.length} item${foods.length !== 1 ? 's' : ''}`}
          size="small"
          sx={{
            bgcolor: alpha(m.color, 0.13),
            color: m.color,
            fontWeight: 600,
            fontSize: 10,
            height: 24,
          }}
        />
      </Box>
      <CardContent sx={{ p: 1, flex: 1, '&:last-child': { pb: 1.5 } }}>
        {foods.length === 0 ? (
          <Box
            sx={{
              border: '1.5px dashed #CBD5E1',
              borderRadius: 2,
              p: 3,
              textAlign: 'center',
              color: '#94A3B8',
              typography: 'body2',
            }}
          >
            No {m.label.toLowerCase()} items planned
          </Box>
        ) : (
          foods.map(f => (
            <FoodCard key={f.TrnFoodWeekCalDtlId} food={f} mealIcon={m.icon} />
          ))
        )}
      </CardContent>
    </StyledMealCard>
  );
}


export default function ResidentFoodMenu() {
  const [weekStart,    setWeekStart]    = useState(() => getWeekStart(new Date()));
  const [selectedDate, setSelectedDate] = useState(() => fmtISO(new Date()));
  const [menuData,     setMenuData]     = useState({});
  const [isLoading,    setIsLoading]    = useState(false);
  const [error,        setError]        = useState(null);

  const location       = useLocation();
  const currentBooking = location.state?.bookingsData?.[0];

  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const today    = fmtISO(new Date());


  const fetchFoodMenuForDate = async (date) => {
    setIsLoading(true);
    setError(null);

   
    const payload = {
      FoodWeekCalDt:   date,
      FoodWeekCalToDt: date,
      PropId:          currentBooking?.PropId ?? '',
      TrnFoodWeekCalId: 0,
      MealTypeId:      0,
      YearId:          0,
      MonthId:         0,
      MonthNm:         '',
      DayNm:           '',
      Flag:            '',
    };

    try {
      const response = await axiosInstance.post(
        'MstFood/RetriveTrnFoodWeekCalAll',
        payload
      );

      if (response.data?.Status === 0 && response.data?.Data?.FoodWeekCalList) {
        const parsed = parseMenu(response.data.Data.FoodWeekCalList);
        setMenuData(prev => ({ ...prev, ...parsed }));
      } else {
        setMenuData(prev => ({ ...prev, [date]: null }));
      }
    } catch (err) {
      console.error('Error fetching food menu data:', err);
      setError('Could not load menu. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (selectedDate) {
      fetchFoodMenuForDate(selectedDate);
    }
  }, [selectedDate, currentBooking?.PropId]);


  function changeWeek(dir) {
    const newWeekStart = addDays(weekStart, dir * 7);
    setWeekStart(newWeekStart);
    const currentDayOfWeek = parseLocalDate(selectedDate).getDay();
    const newDate = addDays(newWeekStart, currentDayOfWeek);
    setSelectedDate(fmtISO(newDate));
  }

  function goToday() {
    setWeekStart(getWeekStart(new Date()));
    setSelectedDate(fmtISO(new Date()));
  }


  const selectedDayData   = menuData[selectedDate];

  const selectedDayObject = parseLocalDate(selectedDate);

  const getDayName = () =>
    selectedDayData?.dayName || DAYS_FULL[selectedDayObject.getDay()];


  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#F1F5F9' }}>

      <Paper
        elevation={0}
        sx={{
          bgcolor: '#606162',
          p: 2,
          position: 'sticky',
          top: 0,
          zIndex: 100,
          borderRadius: 0,
          mt:4
        }}
      >
        <Container maxWidth="xl">
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 2,
            }}
          >
            <Box>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: '#FFFFFF',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <RestaurantIcon /> Food Menu List
              </Typography>
              <Typography variant="caption" sx={{ color: '#94A3B8', mt: 0.5 }}>
                Daily Meal Plan
              </Typography>
            </Box>

          </Box>
        </Container>
      </Paper>

      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 2, mb: 3 }}>
          {weekDays.map((d, i) => {
            const dateStr = fmtISO(d);
            const hasData =
              menuData[dateStr] &&
              Object.keys(menuData[dateStr]?.meals || {}).length > 0;
            const isToday = dateStr === today;
            const active  = dateStr === selectedDate;

            return (
              <StyledDateButton
                key={i}
                active={active ? 1 : 0}
                hasdata={hasData ? 1 : 0}
                onClick={() => setSelectedDate(dateStr)}
              >
                <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>
                  {DAYS_SHORT[d.getDay()]}{isToday && ' •'}
                </Typography>
                <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                  {fmtShort(d)}
                </Typography>
              </StyledDateButton>
            );
          })}
        </Box>

        <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
          <CardContent sx={{ p: 3 }}>
            {isLoading ? (
              <Box sx={{ textAlign: 'center', py: 10 }}>
                <CircularProgress sx={{ color: '#F59E0B' }} />
                <Typography sx={{ mt: 2, color: '#64748B' }}>
                  Fetching menu from server…
                </Typography>
              </Box>
            ) : error ? (
              <Alert
                severity="error"
                sx={{ borderRadius: 2 }}
                action={
                  <Button
                    color="error"
                    size="small"
                    onClick={() => fetchFoodMenuForDate(selectedDate)}
                    startIcon={<RefreshIcon />}
                  >
                    Retry
                  </Button>
                }
              >
                <AlertTitle>Error</AlertTitle>
                {error}
              </Alert>
            ) : (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <Avatar sx={{ width: 50, height: 50, bgcolor: '#FEF3C7', color: '#F59E0B' }}>
                    {MEAL_MAP[2]?.icon}
                  </Avatar>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#1E293B' }}>
                      {getDayName()}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748B' }}>
                      {fmtLong(selectedDayObject)}
                      {selectedDayData?.remark && ` — ${selectedDayData.remark}`}
                    </Typography>
                  </Box>
                </Box>

                {!selectedDayData ||
                !selectedDayData.meals ||
                Object.keys(selectedDayData.meals).length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 8, color: '#94A3B8' }}>
                    <Typography variant="h1" sx={{ fontSize: 48, mb: 2 }}>
                      🍽️
                    </Typography>
                    <Typography>No menu available for this day</Typography>
                  </Box>
                ) : (
                  <Grid container spacing={2}>
                    {MEAL_ORDER.map(mealTypeId => {
                      const foods = selectedDayData.meals[mealTypeId] || [];
                      return (
                        <Grid item xs={12} md={3} key={mealTypeId}>
                          <MealCard mealTypeId={mealTypeId} foods={foods} />
                        </Grid>
                      );
                    })}
                  </Grid>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
}